import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast, SimpleTable, Input, Button } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { createClinicUser, deleteClinicUser, fetchClinicUserFormClinics, fetchClinicUserFormUsers, fetchClinicUsers, findUsersCurrentDefaultClinicUser, updateClinicUser, } from '../../../api/ClinicUserAdminApi';
import { createClinicUserColumns } from './tableColumns';
import styles from './ClinicUsers.module.scss';
const DEFAULT_FILTERS = {
    role: '',
    is_active: '',
};
const ROLE_OPTIONS = ['doctor', 'dvm', 'owner', 'reporter', 'other'];
const normalizeClinicRole = (value) => {
    const cleaned = (value || '').trim().replace(/^['"]+|['"]+$/g, '').toLowerCase();
    return ROLE_OPTIONS.includes(cleaned) ? cleaned : 'other';
};
const getCreateClinicUserErrorMessage = (error) => {
    const payload = error?.response?.body;
    const nonFieldMessage = payload?.nonFieldErrors?.[0] ?? payload?.non_field_errors?.[0];
    if (typeof nonFieldMessage === 'string' && nonFieldMessage.trim()) {
        return nonFieldMessage;
    }
    const firstFieldMessage = payload?.clinic?.[0] ?? payload?.user?.[0];
    if (typeof firstFieldMessage === 'string' && firstFieldMessage.trim()) {
        return firstFieldMessage;
    }
    return 'Failed to create clinic user';
};
const ClinicUsers = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [clinicUsers, setClinicUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingClinicUser, setEditingClinicUser] = useState(null);
    const [editRole, setEditRole] = useState('');
    const [editIsActive, setEditIsActive] = useState(true);
    const [editIsDefault, setEditIsDefault] = useState(false);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [addClinicId, setAddClinicId] = useState('');
    const [addUserId, setAddUserId] = useState('');
    const [addRole, setAddRole] = useState('doctor');
    const [addIsActive, setAddIsActive] = useState(true);
    const [addIsDefault, setAddIsDefault] = useState(false);
    const [addErrorMessage, setAddErrorMessage] = useState('');
    const [isSavingAdd, setIsSavingAdd] = useState(false);
    const [isLoadingAddOptions, setIsLoadingAddOptions] = useState(false);
    const [clinicOptions, setClinicOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const latestRequestIdRef = useRef(0);
    const loadClinicUsers = useCallback(async (search, activeFilters = DEFAULT_FILTERS) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchClinicUsers({
                search: search || undefined,
                role: activeFilters.role || undefined,
                is_active: activeFilters.is_active === '' ? undefined : activeFilters.is_active === 'true',
            });
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            const data = response.body;
            const clinicUserArray = Array.isArray(data) ? data : data.results || [];
            const transformedUsers = clinicUserArray.map((item) => ({
                id: item.id,
                clinic: item.clinic,
                user: item.user,
                user_name: item.userName ?? item.user_name,
                user_email: item.userEmail ?? item.user_email,
                role: normalizeClinicRole(item.role),
                is_active: !!(item.isActive ?? item.is_active),
                is_default: !!(item.isDefault ?? item.is_default),
                created_at: item.createdAt ?? item.created_at,
                created_at_display: item.createdAtDisplay ?? item.created_at_display,
            }));
            setClinicUsers(transformedUsers);
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            console.error('Error loading clinic users:', error);
            showToast({ message: 'Failed to load clinic users', variant: 'error', noBorder: true });
            setClinicUsers([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadClinicUsers(searchTerm, appliedFilters);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadClinicUsers, searchTerm, appliedFilters]);
    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };
    const handleResetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
    };
    // Ask before an action would demote a different clinic's default for this user - and name
    // the clinic, sourced from a live lookup rather than a guess. Fails open on a lookup error
    // (the save proceeds unconfirmed) since the backend enforces the invariant regardless; this
    // check only powers the warning, not the correctness.
    const confirmDefaultDemotionIfNeeded = async (userId, excludeClinicUserId) => {
        try {
            const currentDefault = await findUsersCurrentDefaultClinicUser(userId, excludeClinicUserId);
            if (!currentDefault)
                return true;
            return window.confirm(`This user's current default clinic is ${currentDefault.clinic}. Setting a new default `
                + 'will remove it from there. Continue?');
        }
        catch (error) {
            console.error('Error checking for an existing default clinic:', error);
            return true;
        }
    };
    const runBulkUpdate = async (selectedRows, payload, successNoun) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIdList = selectedRows.map((row) => row.id);
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIdList.map((id) => updateClinicUser(id, payload)));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIdList[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                // Full reload, not a local patch of just the selected rows - "Set Default" can demote
                // a *different*, unselected row for the same user server-side; a local patch would
                // leave that row showing stale "Yes" until a manual refresh.
                await loadClinicUsers(searchTerm, appliedFilters);
                showToast({
                    message: `Updated ${successfulIds.length} clinic user${successfulIds.length === 1 ? '' : 's'} ${successNoun}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error bulk updating clinic users for IDs:', failedIds);
                showToast({
                    message: `Failed to update ${failedIds.length} clinic user${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error running bulk update for clinic users:', error);
            showToast({ message: 'Failed to update selected clinic users', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleOpenEdit = (item) => {
        setEditingClinicUser(item);
        setEditRole(normalizeClinicRole(item.role));
        setEditIsActive(item.is_active !== undefined ? item.is_active : true);
        setEditIsDefault(!!item.is_default);
        setIsEditOpen(true);
    };
    const handleCloseEdit = () => {
        setIsEditOpen(false);
        setEditingClinicUser(null);
        setEditRole('');
        setEditIsActive(true);
        setEditIsDefault(false);
    };
    const loadAddOptions = useCallback(async () => {
        setIsLoadingAddOptions(true);
        try {
            const [usersResponse, clinicsResponse] = await Promise.all([
                fetchClinicUserFormUsers(),
                fetchClinicUserFormClinics(),
            ]);
            const usersData = usersResponse.body;
            const clinicsData = clinicsResponse.body;
            const usersArray = Array.isArray(usersData) ? usersData : usersData.results || [];
            const clinicsArray = Array.isArray(clinicsData) ? clinicsData : clinicsData.results || [];
            const nextUsers = usersArray.map((user) => {
                const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                const displayName = fullName || user.username || user.email || 'Unknown User';
                return {
                    id: user.id,
                    label: `${displayName} (${user.email || 'no-email'})`,
                };
            });
            const nextClinics = clinicsArray.map((clinic) => ({
                id: clinic.id,
                label: clinic.name || 'Unnamed Clinic',
            }));
            nextUsers.sort((a, b) => a.label.localeCompare(b.label));
            nextClinics.sort((a, b) => a.label.localeCompare(b.label));
            setUserOptions(nextUsers);
            setClinicOptions(nextClinics);
        }
        catch (error) {
            console.error('Error loading clinic user form options:', error);
            showToast({ message: 'Failed to load add form options', variant: 'error', noBorder: true });
            setUserOptions([]);
            setClinicOptions([]);
        }
        finally {
            setIsLoadingAddOptions(false);
        }
    }, [showToast]);
    const handleOpenAdd = () => {
        setAddClinicId('');
        setAddUserId('');
        setAddRole('doctor');
        setAddIsActive(true);
        setAddIsDefault(false);
        setAddErrorMessage('');
        setIsAddOpen(true);
        loadAddOptions();
    };
    const handleCloseAdd = () => {
        setIsAddOpen(false);
        setAddClinicId('');
        setAddUserId('');
        setAddRole('doctor');
        setAddIsActive(true);
        setAddIsDefault(false);
        setAddErrorMessage('');
    };
    const handleSaveEdit = async () => {
        if (!editingClinicUser)
            return;
        if (editIsDefault) {
            const canProceed = await confirmDefaultDemotionIfNeeded(editingClinicUser.user, editingClinicUser.id);
            if (!canProceed)
                return;
        }
        setIsSavingEdit(true);
        try {
            const response = await updateClinicUser(editingClinicUser.id, {
                role: normalizeClinicRole(editRole),
                user_is_active: editIsActive,
                is_default: editIsDefault,
            });
            // Full reload, not a local patch of just this row - saving a default here can silently
            // demote a *different* row for the same user server-side, and that row may already be
            // visible in the table (or reopened in this same slide-out next). A local patch only of
            // the row just edited leaves the demoted one showing stale "Yes" until a manual refresh.
            await loadClinicUsers(searchTerm, appliedFilters);
            const demoted = response.body?.demotedDefaultClinic ?? response.body?.demoted_default_clinic;
            showToast({
                message: demoted
                    ? `Clinic user updated. Their previous default (${demoted.name}) was updated.`
                    : 'Clinic user updated successfully',
                variant: demoted ? 'warning' : 'success',
                noBorder: true,
            });
            handleCloseEdit();
        }
        catch (error) {
            console.error('Error updating clinic user:', error);
            showToast({ message: 'Failed to update clinic user', variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingEdit(false);
        }
    };
    const handleSaveAdd = async () => {
        if (!addClinicId || !addUserId) {
            setAddErrorMessage('Please select both clinic and user');
            showToast({ message: 'Please select both clinic and user', variant: 'error', noBorder: true });
            return;
        }
        if (addIsDefault) {
            const canProceed = await confirmDefaultDemotionIfNeeded(addUserId);
            if (!canProceed)
                return;
        }
        setIsSavingAdd(true);
        setAddErrorMessage('');
        try {
            const response = await createClinicUser({
                clinic: addClinicId,
                user: addUserId,
                role: normalizeClinicRole(addRole),
                user_is_active: addIsActive,
                is_default: addIsDefault,
            });
            const demoted = response.body?.demotedDefaultClinic ?? response.body?.demoted_default_clinic;
            showToast({
                message: demoted
                    ? `Clinic user created. Their previous default (${demoted.name}) was updated.`
                    : 'Clinic user created successfully',
                variant: demoted ? 'warning' : 'success',
                noBorder: true,
            });
            handleCloseAdd();
            loadClinicUsers(searchTerm, appliedFilters);
        }
        catch (error) {
            console.error('Error creating clinic user:', error);
            const message = getCreateClinicUserErrorMessage(error);
            setAddErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingAdd(false);
        }
    };
    const handleBulkDelete = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIdList = selectedRows.map((row) => row.id);
        if (!window.confirm(`Are you sure you want to delete ${selectedIdList.length} selected clinic user${selectedIdList.length === 1 ? '' : 's'}?`)) {
            return;
        }
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIdList.map((id) => deleteClinicUser(id)));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIdList[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setClinicUsers((prev) => prev.filter((item) => !successfulIds.includes(item.id)));
                showToast({
                    message: `Deleted ${successfulIds.length} clinic user${successfulIds.length === 1 ? '' : 's'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error deleting clinic users for IDs:', failedIds);
                showToast({
                    message: `Failed to delete ${failedIds.length} clinic user${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error deleting selected clinic users:', error);
            showToast({ message: 'Failed to delete selected clinic users', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleBulkSetDefault = (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        if (!window.confirm('This may change the default clinic for any selected user who already has one. Continue?')) {
            return;
        }
        runBulkUpdate(selectedRows, { is_default: true }, 'as default');
    };
    const columns = createClinicUserColumns();
    const bulkActions = [
        {
            label: 'Delete',
            action: (selectedRows) => handleBulkDelete(selectedRows),
        },
        {
            label: 'Activate',
            action: (selectedRows) => runBulkUpdate(selectedRows, { user_is_active: true }, 'as active'),
        },
        {
            label: 'Deactivate',
            action: (selectedRows) => runBulkUpdate(selectedRows, { user_is_active: false }, 'as inactive'),
        },
        {
            label: 'Set Default',
            action: (selectedRows) => handleBulkSetDefault(selectedRows),
        },
        {
            label: 'Unset Default',
            action: (selectedRows) => runBulkUpdate(selectedRows, { is_default: false }, 'as not default'),
        },
        ...ROLE_OPTIONS.map((role) => {
            const capitalizedRole = role.charAt(0).toUpperCase() + role.slice(1);
            return {
                label: `Set Role: ${capitalizedRole}`,
                action: (selectedRows) => runBulkUpdate(selectedRows, { role }, `role to ${capitalizedRole}`),
            };
        }),
    ];
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "clinic-user-role", children: "Role" }), _jsxs("select", { id: "clinic-user-role", value: filters.role, onChange: (event) => setFilters((prev) => ({ ...prev, role: event.target.value })), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "doctor", children: "Doctor" }), _jsx("option", { value: "dvm", children: "DVM" }), _jsx("option", { value: "owner", children: "Owner" }), _jsx("option", { value: "reporter", children: "Reporter" }), _jsx("option", { value: "other", children: "Other" })] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "clinic-user-is-active", children: "Is Active" }), _jsxs("select", { id: "clinic-user-is-active", value: filters.is_active, onChange: (event) => setFilters((prev) => ({ ...prev, is_active: event.target.value })), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "true", children: "Active" }), _jsx("option", { value: "false", children: "Inactive" })] })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply Filters", onClick: handleApplyFilters }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleResetFilters })] }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by clinic, user name, email, or role", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) })] }), _jsx(SimpleTable, { columns: columns, data: clinicUsers, rowHeight: "60px", tableHeading: "Clinic Users", titleStyle: {
                            color: '#000',
                            fontFamily: 'Nunito',
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '28px',
                        }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Clinic users per page", pageSize: 20, onRowClick: (item) => handleOpenEdit(item), bulkActions: bulkActions, customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Clinic User", onClick: handleOpenAdd }) })] }), isEditOpen && editingClinicUser && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseEdit }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Edit Clinic User" }) }), _jsxs("div", { className: styles.slideOutContent, children: [_jsxs("div", { className: styles.readOnlyField, children: [_jsx("span", { className: styles.fieldLabel, children: "Clinic" }), _jsx("span", { children: editingClinicUser.clinic || 'N/A' })] }), _jsxs("div", { className: styles.readOnlyField, children: [_jsx("span", { className: styles.fieldLabel, children: "User" }), _jsx("span", { children: editingClinicUser.user_name || editingClinicUser.user_email || 'N/A' })] }), _jsxs("div", { className: styles.readOnlyField, children: [_jsx("span", { className: styles.fieldLabel, children: "Email" }), _jsx("span", { children: editingClinicUser.user_email || 'N/A' })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: editIsActive, onChange: (event) => setEditIsActive(event.target.checked) }), "Is Active"] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-clinic-user-role", children: "Role" }), _jsx("select", { id: "edit-clinic-user-role", value: editRole, onChange: (event) => setEditRole(event.target.value), children: ROLE_OPTIONS.map((role) => (_jsx("option", { value: role, children: role.charAt(0).toUpperCase() + role.slice(1) }, role))) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: editIsDefault, onChange: (event) => setEditIsDefault(event.target.checked) }), "Default clinic user"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseEdit }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingEdit ? 'Saving...' : 'Save Changes', onClick: handleSaveEdit, disabled: isSavingEdit })] })] })] })), isAddOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseAdd }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Add Clinic User" }) }), _jsxs("div", { className: styles.slideOutContent, children: [isLoadingAddOptions && _jsx("div", { children: "Loading options..." }), !!addErrorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: addErrorMessage })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-clinic-user-clinic", children: "Clinic" }), _jsxs("select", { id: "add-clinic-user-clinic", value: addClinicId, onChange: (event) => {
                                                    setAddClinicId(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                }, children: [_jsx("option", { value: "", children: "Select clinic" }), clinicOptions.map((option) => (_jsx("option", { value: option.id, children: option.label }, option.id)))] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-clinic-user-user", children: "User" }), _jsxs("select", { id: "add-clinic-user-user", value: addUserId, onChange: (event) => {
                                                    setAddUserId(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                }, children: [_jsx("option", { value: "", children: "Select user" }), userOptions.map((option) => (_jsx("option", { value: option.id, children: option.label }, option.id)))] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-clinic-user-role", children: "Role" }), _jsx("select", { id: "add-clinic-user-role", value: addRole, onChange: (event) => setAddRole(event.target.value), children: ROLE_OPTIONS.map((role) => (_jsx("option", { value: role, children: role.charAt(0).toUpperCase() + role.slice(1) }, role))) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: addIsActive, onChange: (event) => setAddIsActive(event.target.checked) }), "Is Active"] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: addIsDefault, onChange: (event) => setAddIsDefault(event.target.checked) }), "Default clinic user"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseAdd }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingAdd ? 'Saving...' : 'Create Clinic User', onClick: handleSaveAdd, disabled: isSavingAdd || isLoadingAddOptions })] })] })] }))] }));
};
export default ClinicUsers;
